import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Download,
  ChevronDown,
  ChevronUp,
  FileText,
  Calendar,
  User,
  Building,
  Mail,
  MapPin,
  CreditCard
} from 'lucide-react';
import EarlyPaymentBanner from '../shared/EarlyPaymentBanner';

const MobileInvoicePreview = ({ invoice, isOpen, onClose, onDownloadPDF, title = "Invoice Preview", loading = false, viewType = "receiver" }) => {
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    items: true,
    payment: false,
    attachments: false
  });

  if (!isOpen) {
    console.log('📱 MobileInvoicePreview: Not rendering - isOpen:', isOpen);
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <AnimatePresence>
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end">
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="bg-white w-full max-h-[90vh] rounded-t-3xl flex flex-col"
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between rounded-t-3xl z-10">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[#1F11ED] mx-auto mb-4"></div>
                <h3 className="text-lg font-medium text-gray-900">Loading...</h3>
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  }

  if (!invoice) {
    console.log('📱 MobileInvoicePreview: No invoice data provided');
    return (
      <AnimatePresence>
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end">
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="bg-white w-full max-h-[90vh] rounded-t-3xl flex flex-col"
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between rounded-t-3xl z-10">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Invoice Data</h3>
                <p className="text-sm text-gray-600">Unable to load invoice details</p>
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  }

  // Debug: Log all invoice fields
  console.log('📱 MobileInvoicePreview: Rendering with invoice:', invoice);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatCurrency = (amount) => {
    // Handle different data formats - amount might be in pence or pounds
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
    
    // Database stores amounts as DECIMAL - already in pounds, not pence
    // Don't divide by 100
    if (numAmount < 0) {
      return `(£${Math.abs(numAmount).toFixed(2)})`;
    }
    return `£${numAmount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      pending_approval: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      overdue: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const calculateLineItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unitPrice || 0);
    const discount = subtotal * ((item.discount || 0) / 100);
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * ((item.taxRate || 0) / 100);
    return afterDiscount + tax;
  };

  const calculateInvoiceTotal = () => {
    if (!invoice.lineItems || !Array.isArray(invoice.lineItems)) return 0;
    return invoice.lineItems.reduce((total, item) => total + calculateLineItemTotal(item), 0);
  };

  const CollapsibleSection = ({ title, isExpanded, onToggle, children }) => (
    <div className="border-b border-gray-200">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
      >
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-end">
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="bg-white w-full max-h-[90vh] rounded-t-3xl flex flex-col"
        >
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Header - Scrolls with content */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between rounded-t-3xl">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            {/* Early Payment Banner (Mobile Full) */}
            <div className="p-4 bg-gray-50">
              <EarlyPaymentBanner 
                invoice={invoice} 
                viewType={viewType} 
                compact={false}
              />
            </div>

            {/* Invoice Header - Always Visible */}
            <div className="bg-gradient-to-br from-[#1F11ED] to-[#1709C7] text-white p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-white/70 mb-1">Invoice Number</p>
                  <h1 className="text-2xl font-bold">{invoice.invoiceNumber || 'N/A'}</h1>
                </div>
                <FileText className="w-12 h-12 text-white/30" />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/70 mb-1">Total Amount</p>
                  <p className="text-3xl font-bold">{formatCurrency(invoice.amount || invoice.total || calculateInvoiceTotal())}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/70 mb-1">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                    {(invoice.status || 'pending').replace(/_/g, ' ').toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Rejection Reason Alert - Only shown on preview, NOT on PDF */}
            {(invoice.status === 'rejected' || invoice.status === 'unapproved') && (invoice.rejectionReason || invoice.rejectReason) && (
              <div className="mx-4 mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-red-800 mb-1">
                      {invoice.status === 'rejected' ? 'Rejection Reason' : 'Unapproval Reason'}
                    </h4>
                    <p className="text-sm text-red-700 whitespace-pre-wrap">
                      {invoice.rejectionReason || invoice.rejectReason}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Invoice Details Section */}
            <CollapsibleSection
              title="Invoice Details"
              isExpanded={expandedSections.details}
              onToggle={() => toggleSection('details')}
            >
              <div className="space-y-4">
                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Issue Date</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(invoice.issueDate || invoice.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <p className="text-xs text-gray-500">Due Date</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(invoice.dueDate)}</p>
                    </div>
                  </div>
                </div>

                {/* From (Sender) */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    <p className="text-xs font-medium text-gray-500 uppercase">From</p>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {invoice.sender?.companyName || invoice.sender?.name || 
                     (invoice.sender?.firstName && invoice.sender?.lastName ? 
                     `${invoice.sender.firstName} ${invoice.sender.lastName}` :
                     'Unknown Sender')}
                  </p>
                  {invoice.sender?.email && (
                    <div className="flex items-center space-x-1 mt-1">
                      <Mail className="w-3 h-3 text-gray-400" />
                      <p className="text-sm text-gray-600">{invoice.sender.email}</p>
                    </div>
                  )}
                  {(invoice.sender?.address || invoice.sender?.city || invoice.sender?.state || invoice.sender?.zipCode || invoice.sender?.country) && (
                    <div className="flex items-start space-x-1 mt-1">
                      <MapPin className="w-3 h-3 text-gray-400 mt-1" />
                      <div className="text-sm text-gray-600">
                        {invoice.sender?.address && <p>{invoice.sender.address}</p>}
                        {(invoice.sender?.city || invoice.sender?.state || invoice.sender?.zipCode) && (
                          <p>
                            {[invoice.sender?.city, invoice.sender?.state, invoice.sender?.zipCode].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {invoice.sender?.country && <p>{invoice.sender.country}</p>}
                      </div>
                    </div>
                  )}
                </div>

                {/* To (Receiver) */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <p className="text-xs font-medium text-gray-500 uppercase">To</p>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {invoice.receiver?.companyName || invoice.receiver?.name || 
                     invoice.customer?.companyName || invoice.customer?.company || 
                     invoice.customer?.name || 
                     (invoice.receiver?.firstName && invoice.receiver?.lastName ? 
                     `${invoice.receiver.firstName} ${invoice.receiver.lastName}` :
                     'Unknown Receiver')}
                  </p>
                  {(invoice.receiver?.email || invoice.customer?.email) && (
                    <div className="flex items-center space-x-1 mt-1">
                      <Mail className="w-3 h-3 text-gray-400" />
                      <p className="text-sm text-gray-600">{invoice.receiver?.email || invoice.customer?.email}</p>
                    </div>
                  )}
                  {(invoice.receiver?.address || invoice.receiver?.city || invoice.receiver?.state || invoice.receiver?.zipCode || invoice.receiver?.country ||
                    invoice.customer?.address || invoice.customer?.city || invoice.customer?.state || invoice.customer?.zipCode || invoice.customer?.country) && (
                    <div className="flex items-start space-x-1 mt-1">
                      <MapPin className="w-3 h-3 text-gray-400 mt-1" />
                      <div className="text-sm text-gray-600">
                        {(invoice.receiver?.address || invoice.customer?.address) && (
                          <p>{invoice.receiver?.address || invoice.customer?.address}</p>
                        )}
                        {((invoice.receiver?.city || invoice.receiver?.state || invoice.receiver?.zipCode) ||
                          (invoice.customer?.city || invoice.customer?.state || invoice.customer?.zipCode)) && (
                          <p>
                            {[
                              invoice.receiver?.city || invoice.customer?.city,
                              invoice.receiver?.state || invoice.customer?.state,
                              invoice.receiver?.zipCode || invoice.customer?.zipCode
                            ].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {(invoice.receiver?.country || invoice.customer?.country) && (
                          <p>{invoice.receiver?.country || invoice.customer?.country}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                {invoice.description && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Description</p>
                    <p className="text-sm text-gray-700">{invoice.description}</p>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Line Items Section */}
            <CollapsibleSection
              title={`Line Items (${invoice.lineItems?.length || 0})`}
              isExpanded={expandedSections.items}
              onToggle={() => toggleSection('items')}
            >
              <div className="space-y-3">
                {invoice.lineItems && invoice.lineItems.length > 0 ? (
                  invoice.lineItems.map((item, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{item.description || item.name || 'Item'}</p>
                          {item.details && (
                            <p className="text-xs text-gray-600 mt-1">{item.details}</p>
                          )}
                        </div>
                        <p className="font-bold text-gray-900">{formatCurrency(calculateLineItemTotal(item))}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div className="flex justify-between">
                          <span>Quantity:</span>
                          <span className="font-medium">{item.quantity || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Unit Price:</span>
                          <span className="font-medium">{formatCurrency(item.unitPrice || 0)}</span>
                        </div>
                        {item.discount > 0 && (
                          <div className="flex justify-between text-orange-600">
                            <span>Discount:</span>
                            <span className="font-medium">{item.discount}%</span>
                          </div>
                        )}
                        {item.taxRate > 0 && (
                          <div className="flex justify-between">
                            <span>Tax:</span>
                            <span className="font-medium">{item.taxRate}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No line items</p>
                )}

                {/* Totals */}
                {((invoice.lineItems && invoice.lineItems.length > 0) || invoice.amount) && (
                  <div className="bg-[#1F11ED] text-white p-4 rounded-lg mt-4">
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-semibold">Total</p>
                      <p className="text-2xl font-bold">{formatCurrency(invoice.amount || invoice.total || calculateInvoiceTotal())}</p>
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Payment Info Section */}
            <CollapsibleSection
              title="Payment Information"
              isExpanded={expandedSections.payment}
              onToggle={() => toggleSection('payment')}
            >
              <div className="space-y-3">
                {invoice.paymentDate && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Payment Date</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{formatDate(invoice.paymentDate)}</span>
                  </div>
                )}
                
                {invoice.paymentMethod && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Payment Method</span>
                    <span className="text-sm font-medium text-gray-900 capitalize">{invoice.paymentMethod}</span>
                  </div>
                )}

                {invoice.transactionId && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Transaction ID</span>
                    <span className="text-xs font-mono text-gray-900">{invoice.transactionId}</span>
                  </div>
                )}

                {(!invoice.paymentDate && !invoice.paymentMethod && !invoice.transactionId) && (
                  <p className="text-sm text-gray-500 text-center py-4">No payment information available</p>
                )}
              </div>
            </CollapsibleSection>

            {/* Attachments Section */}
            {invoice.attachments && invoice.attachments.length > 0 && (
              <CollapsibleSection
                title={`📎 Attachments (${invoice.attachments.length})`}
                isExpanded={expandedSections.attachments}
                onToggle={() => toggleSection('attachments')}
              >
                <div className="space-y-2">
                  {invoice.attachments.map((attachment, index) => {
                    const handleDownloadAttachment = async () => {
                      try {
                        const response = await fetch(`/api/files/download/${encodeURIComponent(attachment.s3Key)}?filename=${encodeURIComponent(attachment.name)}`, {
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                          }
                        });
                        
                        if (response.ok) {
                          const data = await response.json();
                          const link = document.createElement('a');
                          link.href = data.downloadUrl;
                          link.download = attachment.name;
                          link.target = '_blank';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        } else {
                          alert('Failed to download file. Please try again.');
                        }
                      } catch (error) {
                        console.error('Error downloading attachment:', error);
                        alert('Error downloading file. Please try again.');
                      }
                    };
                    
                    const formatFileSize = (bytes) => {
                      if (bytes < 1024) return bytes + ' B';
                      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
                    };
                    
                    return (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{attachment.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={handleDownloadAttachment}
                          className="ml-3 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200 text-xs flex items-center space-x-1 flex-shrink-0"
                        >
                          <Download className="w-3 h-3" />
                          <span>Download</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>
            )}
          </div>

          {/* Sticky Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 space-y-3">
            <button
              onClick={onDownloadPDF}
              className="w-full flex items-center justify-center space-x-2 bg-[#1F11ED] text-white py-3 px-4 rounded-xl font-medium hover:bg-[#1709C7] active:scale-95 transition-all"
            >
              <Download className="w-5 h-5" />
              <span>Download PDF</span>
            </button>
            
            <button
              onClick={onClose}
              className="w-full text-gray-600 py-2 px-4 rounded-xl font-medium hover:bg-gray-100 active:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default MobileInvoicePreview;

